import { Component, ElementRef, HostListener, OnInit, ViewChild } from "@angular/core";
import { catchError, map, of, throwError } from "rxjs";
import { ActivatedRoute, Router } from "@angular/router";
import { GenreService } from "src/app/services/genre.service";
import { FaqService } from "src/app/services/faq.service";
import { titleSplit } from "src/app/utils/title-split";
import { ConfirmationModalComponent } from "src/app/components/common/confirmation-modal/confirmation-modal.component";
import { MatDialog } from "@angular/material/dialog";
import { Meta, Title } from "@angular/platform-browser";
import { AuthService } from "src/app/services/auth.service";
import { SignInComponent } from "../../login/sign-in/sign-in.component";
import { PaymentService } from "src/app/services/payment.service";
import { RazorpayComponent } from "../../common/razorpay/razorpay.component";
import { SeoServiceService } from "src/app/services/seo-service.service";

@Component({
  selector: "app-genredetail",
  templateUrl: "./genredetail.component.html",
  styleUrls: ["./genredetail.component.scss"],
})
export class GenredetailComponent implements OnInit {
  @ViewChild('videoPlayer') videoPlayer!: ElementRef<HTMLVideoElement>;
  @ViewChild('demoVideo') demoVideo!: ElementRef<HTMLVideoElement>;
  display = false;

  availableQualities: string[] = ['Auto', '480p', '720p', '1080p', '1920p (Full HD)'];
  selectedQuality: string = 'Auto';
  showQualityMenu: boolean = false;

  @HostListener('document:click', ['$event'])
  onDocumentClick(): void {
    this.showQualityMenu = false;
    const player = (this.videoPlayer || this.demoVideo)?.nativeElement;
    if (player && player.muted && !player.paused) {
      player.muted = false;
    }
  }

  toggleQualityMenu(event: Event): void {
    event.stopPropagation();
    this.showQualityMenu = !this.showQualityMenu;
  }

  getQualityClass(): string {
    switch (this.selectedQuality) {
      case '480p':
        return 'quality-480p';
      case '720p':
        return 'quality-720p';
      case '1080p':
        return 'quality-1080p';
      case '1920p (Full HD)':
        return 'quality-1920p';
      default:
        return 'quality-Auto';
    }
  }

  changeQuality(quality: string): void {
    if (this.selectedQuality === quality) {
      this.showQualityMenu = false;
      return;
    }

    const player = this.videoPlayer?.nativeElement;
    const currentTime = player ? player.currentTime : 0;
    const isPaused = player ? player.paused : true;

    this.selectedQuality = quality;
    this.showQualityMenu = false;

    if (player) {
      const targetSrc = this.getVideoSource();

      if (player.src !== targetSrc) {
        player.pause();

        const onLoadedMetadata = () => {
          player.removeEventListener('loadedmetadata', onLoadedMetadata);
          try {
            if (currentTime > 0 && !isNaN(player.duration) && currentTime < player.duration) {
              player.currentTime = currentTime;
            }
          } catch (err) {
            console.warn('Unable to seek video to currentTime:', err);
          }
          if (!isPaused) {
            player.play().catch(err => console.warn('Quality change playback error:', err));
          }
        };

        const onError = () => {
          player.removeEventListener('error', onError);
          player.removeEventListener('loadedmetadata', onLoadedMetadata);
          console.warn('Resolution variant URL not found on server, applying quality mode to base stream.');
          const fallbackSrc = this.getVideoSource(true);
          if (player.src !== fallbackSrc) {
            player.src = fallbackSrc;
            player.load();
            if (!isPaused) {
              player.play().catch(e => console.warn('Fallback play error:', e));
            }
          }
        };

        player.addEventListener('loadedmetadata', onLoadedMetadata);
        player.addEventListener('error', onError, { once: true });

        player.src = targetSrc;
        player.load();
      }
    }
  }

  getVideoSource(forceBase: boolean = false): string {
    if (!this.genrelist) return '';
    const baseVideo = this.genrelist.demo_video || '';

    if (forceBase || this.selectedQuality === 'Auto' || !baseVideo) {
      return baseVideo;
    }

    if (this.genrelist.qualities && this.genrelist.qualities[this.selectedQuality]) {
      return this.genrelist.qualities[this.selectedQuality];
    }

    const qualityKeyMap: { [key: string]: string[] } = {
      '480p': ['video_480p', 'video_480', 'url_480p'],
      '720p': ['video_720p', 'video_720', 'url_720p'],
      '1080p': ['video_1080p', 'video_1080', 'url_1080p'],
      '1920p (Full HD)': ['video_1920p', 'video_1920', 'video_1080p', 'url_1920p']
    };

    const keys = qualityKeyMap[this.selectedQuality] || [];
    for (const key of keys) {
      if (this.genrelist[key]) {
        return this.genrelist[key];
      }
    }

    const suffixMap: { [key: string]: string } = {
      '480p': '_480p',
      '720p': '_720p',
      '1080p': '_1080p',
      '1920p (Full HD)': '_1920p'
    };

    const suffix = suffixMap[this.selectedQuality];
    if (suffix && baseVideo.includes('.mp4') && !baseVideo.includes('X-Amz-Signature') && !baseVideo.includes('Signature=')) {
      const parts = baseVideo.split('.mp4');
      return `${parts[0]}${suffix}.mp4${parts.slice(1).join('.mp4')}`;
    }

    return baseVideo;
  }

  play() {
    this.display = true;
    const player = (this.videoPlayer || this.demoVideo)?.nativeElement;
    if (player) {
      player.muted = false;
      player.load();
      const playPromise = player.play();
      if (playPromise !== undefined) {
        playPromise.catch(err => {
          console.warn('Unmuted play restricted by browser, playing muted until user gesture:', err);
          player.muted = true;
          player.play().catch(e => console.warn('Muted play error:', e));
        });
      }
    }
  }
  genrelist: any;
  faqList: any;
  genreName: string | null = null;
  activeIndex: number | null = null;

  title = new Map<string, string>([
    [
      "learn-flute-online",
      "Flute Classes In Noida, Start Learning Online Today",
    ],
    [
      "bharatnatyam-online-classes",
      "Bharatanatyam Online Classes in Noida, Bharatnatyam Dance Classes NCR",
    ],
    [
      "keyboard-online-classes",
      "Keyboard Classes Online Noida, Keyboard Classes Near Me",
    ],
    [
      "learn-harmonium-online",
      "Harmonium Classes in Noida & Delhi NCR, Harmonium Classes Near Me",
    ],
    [
      "online-guitar-classes",
      "Best Guitar Classes in Delhi NCR , Online Guitar Classes Noida",
    ],
    [
      "carnatic-vocal-classes",
      "Carnatic Music Classes Near Me, Carnatic Vocal Classes in Noida",
    ],
    [
      "hindustani-vocal-classes",
      "Hindustani Vocal Music Noida, Hindustani Music Classes Near Me",
    ],
    [
      "online-sitar-classes",
      "Sitar Classes Near Me Noida, Delhi - Best Sitar Classes",
    ],
    [
      "kathak-dance-online-classes",
      "Kathak Dance Online Classes Noida, Join Online Dance Class",
    ],
    [
      "online-theatre-courses",
      "Theatre Classes Online Noida, Delhi, Theatre Classes Near Me",
    ],
    [
      "tabla-classes-online",
      "Tabla Classes Online Noida & Delhi, Tabla Classes Near Me",
    ],
  ]);

  keywordMeta = new Map<string, string>([
    [
      "learn-flute-online",
      "Flute course, online flute classes, learn flute, online flute lessons",
    ],
    [
      "bharatnatyam-online-classes",
      "Bharatanatyam class, bharatnatyam classes, online bharatanatyam class, bharatanatyam near me",
    ],
    [
      "keyboard-online-classes",
      "Online keyboard lessons, online keyboard classes",
    ],
    [
      "learn-harmonium-online",
      "Harmonium classes, harmonium course online, learn harmonium online",
    ],
    [
      "online-guitar-classes",
      "Guitar course, learn guitar online, play guitar online, online guitar classes",
    ],
    [
      "carnatic-vocal-classes",
      "Carnatic vocal class, hindustani vocal classes, carnatic classes online, carnatic music",
    ],
    [
      "hindustani-vocal-classes",
      "Hindustani vocal classes, hindustani singing classes, hindustani music classes",
    ],
    [
      "online-sitar-classes",
      "Learn sitar, sitar lessons, sitar tutor, learn sitar online, online sitar classes",
    ],
    [
      "kathak-dance-online-classes",
      "Kathak class, kathak dance, kathak classes, learn kathak online, kathak online course",
    ],
    [
      "online-theatre-courses",
      "Theatre class, drama course, theatre online classes",
    ],
    [
      "tabla-classes-online",
      "Tabla Class, tabla online course, tabla classes online",
    ],
  ]);

  descriptionMeta = new Map<string, string>([
    [
      "learn-flute-online",
      "Master the flute online classes with Virsa! Learn easily from home and elevate your flute musical journey.",
    ],
    [
      "bharatnatyam-online-classes",
      "Master Bharatanatyam from home with expert-led Online Classes with Virsa. Join Bharatnatyam Dance Classes at your Home, anytime, anywhere.",
    ],
    [
      "keyboard-online-classes",
      "Find Keyboard Classes near me for a seamless learning experience. Join now for flexible sessions. Best keyboard online classes for beginners in  Noida & Delhi",
    ],
    [
      "learn-harmonium-online",
      "Master the Harmonium from anywhere with our online classes Noida & Delhi! With expert guidance Virsa. Join Harmonium Classes near me you today.",
    ],
    [
      "online-guitar-classes",
      "Master Guitar with top-notch online classes Delhi NCR! Learn chords, scales & more at your home. Join Now Online Guitar Classes Noida.",
    ],
    [
      "carnatic-vocal-classes",
      "Looking for Carnatic Music Classes near me you with expert-led Vocal online classes Noida & Delhi. Join Carnatic music online classes on Virsa",
    ],
    [
      "hindustani-vocal-classes",
      "Find Hindustani Vocal Music through specialized classes Noida. Carnatic Vocal Classes, Hindustani Vocal Classes, Indian Classical For Beginners Delhi NCR.",
    ],
    [
      "online-sitar-classes",
      "Master the art of Sitar from anywhere! Join our top-notch Best Sitar Classes for beginners  Noida & Experts. Enroll now Sitar Classes online Delhi NCR.",
    ],
    [
      "kathak-dance-online-classes",
      "Find Best Kathak dance online classes Delhi NCR. Learn from experts, Master techniques, And embrace the beauty of this India classical dance form Noida.",
    ],
    [
      "online-theatre-courses",
      "Master the art of acting with our premier Theatre Classes near me. Theatre Classes In Noida & Delhi That Will Nurture The Actor In You",
    ],
    [
      "tabla-classes-online",
      "Looking for a Tabla online classes Noida & Delhi NCR? Learn from Best Teachers near you. Join the best Tabla classes online for all age groups.",
    ],
  ]);

  myMap = new Map<string, string>([
    ["learn-flute-online", "Flute"],
    ["bharatnatyam-online-classes", "Bharatnatyam"],
    ["keyboard-online-classes", "Keyboard"],
    ["learn-harmonium-online", "Harmonium"],
    ["online-guitar-classes", "Guitar"],
    ["carnatic-vocal-classes", "Carnatic Vocal"],
    ["hindustani-vocal-classes", "Hindustani Classical Vocal"],
    ["online-sitar-classes", "Sitar"],
    ["kathak-dance-online-classes", "Kathak"],
    ["online-theatre-courses", "Theatre"],
    ["tabla-classes-online", "Tabla"],
  ]);

  allgenrelist = [
    { id: "learn-flute-online", name: "Flute" },
    { id: "bharatnatyam-online-classes", name: "Bharatnatyam" },
    { id: "keyboard-online-classes", name: "Keyboard" },
    { id: "learn-harmonium-online", name: "Harmonium" },
    { id: "online-guitar-classes", name: "Guitar" },
    { id: "carnatic-vocal-classes", name: "Carnatic Vocal" },
    { id: "hindustani-vocal-classes", name: "Hindustani Classical Vocal" },
    { id: "online-sitar-classes", name: "Sitar" },
    { id: "kathak-dance-online-classes", name: "Kathak" },
    { id: "online-theatre-courses", name: "Theatre" },
    { id: "tabla-classes-online", name: "Tabla" },
  ];

  constructor(
    private ativatedRoute: ActivatedRoute,
    private genre: GenreService,
    private auth: AuthService,
    private pay: PaymentService,
    private faq: FaqService,
    private dialog: MatDialog,
    private titleService: Title,
    private router: Router,
    private meta: Meta,
    private seoService: SeoServiceService
  ) { }

  ngOnInit(): void {
    this.ativatedRoute.paramMap.subscribe((params) => {
      this.genreName = params.get("id");

      if (!this.genreName) {
        this.router.navigate(["/404"]);
        return;
      }

      const fallbackTitle = this.genreName
        .replace(/-/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase());
      const metaTitle =
        this.title.get(this.genreName) ??
        `${fallbackTitle} Online Classes - Virsa`;
      const metaKeywords =
        this.keywordMeta.get(this.genreName) ??
        `${fallbackTitle}, online classes, Virsa`;
      const metaDescription =
        this.descriptionMeta.get(this.genreName) ??
        `Join our online ${fallbackTitle} course and start learning from the comfort of your home.`;

      this.titleService.setTitle(metaTitle);
      this.meta.updateTag({ name: "keywords", content: metaKeywords });
      this.meta.updateTag({ name: "description", content: metaDescription });

      this.seoService.createLinkForCanonicalURL();
      this.getFaqList();
      this.genrelist = null;
      this.checkLogin(true);
    });
  }

  getGenere(isLoggedIn: boolean) {
    if (!this.genreName) return;

    const fallbackGenre = this.genreName.replace(/-/g, " ");
    const mappedGenre = this.myMap.get(this.genreName) ?? fallbackGenre;
    const loginStatus = isLoggedIn ? "yes" : "not";

    this.genre
      .getgenreByName(mappedGenre, loginStatus)
      .pipe(
        map((res: any) => {
          if (!res?.details) throw new Error("Genre not found");
          this.genrelist = res.details;
        }),
        catchError((error) => {
          this.router.navigate(["/404"]);
          return of(null);
        })
      )
      .subscribe();
  }

  getFaqList() {
    this.faq
      .getfaq()
      .pipe(
        map((res: any) => {
          this.faqList = res.details;
        }),
        catchError((error) => {
          return throwError(error.message || error);
        })
      )
      .subscribe();
  }

  toggleAccordion(index: number): void {
    this.activeIndex = this.activeIndex === index ? null : index;
  }

  gettitle(title: string | null, part = false): string {
    return titleSplit(title, part);
  }

  openDialog() {
    this.dialog.open(ConfirmationModalComponent, {
      enterAnimationDuration: "350ms",
      data: { error: null },
    });
  }

  checkLogin(start: boolean) {
    const isLoggedIn = this.auth.isLoggedIn();
    if (!start && !isLoggedIn) {
      this.openLogIn();
    } else if (!start) {
      this.initCoursePayment(this.genrelist.id, this.genrelist.discount_cost);
    }
    this.getGenere(isLoggedIn);
  }

  openLogIn() {
    const dialogRef = this.dialog.open(SignInComponent, {
      enterAnimationDuration: "200ms",
    });

    dialogRef.afterClosed().subscribe(() => {
      window.location.reload();
    });
  }

  initCoursePayment(course_id: number, amount: number) {
    this.pay
      .initCoursePayment(course_id)
      .pipe(
        map((res: any) => {
          if (!res.error) {
            this.initPayment(res.details.id, amount);
          }
        }),
        catchError((error) => {
          return throwError(error.message || error);
        })
      )
      .subscribe();
  }

  initPayment(order_id: number, amount: number) {
    this.pay
      .initPayment({ rc_order_id: order_id })
      .pipe(
        map((res: any) => {
          this.payNow(res.details.order_id, amount);
        }),
        catchError((error) => {
          return throwError(error.message || error);
        })
      )
      .subscribe();
  }

  payNow(order_id: string, amount: number) {
    const dialogRef = this.dialog.open(RazorpayComponent, {
      enterAnimationDuration: "200ms",
      data: { id: order_id, amount },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.getGenere(true);
    });
  }

  handleButtonClick() {
    if (this.genrelist.is_purchased && !this.genrelist.is_course_expired) {
      this.router.navigate(
        ["/profile/genre/genre-details/" + this.genreName + "/playlist"],
        {
          state: {
            playlist: this.genrelist?.courses_list,
            name: this.genrelist?.genre,
          },
        }
      );
    } else {
      this.checkLogin(false);
    }
  }

  getImage(genre: string) {
    return `https://d2abnqj5vuy2s4.cloudfront.net/assets/images/genre/${genre
      .replace(/ /g, "_")
      .toLowerCase()}.png`;
  }
}
